import React, { Component, useEffect, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { TextInput } from "@mantine/core"

import { Icon } from "quorumdesign"

// Below components use connect with export default
/* eslint-disable import/no-named-as-default */
import ListView from "shared/search/containers/ListView/index"
import ListViewDisplayWrapper from "app/static/frontend/search-new/components/ListView/ListViewDisplayWrapper"
import SearchActionButtons from "app/static/frontend/search-new/components/FilterableListView/SearchActionButtons"
import SearchCountBar from "app/static/frontend/search-new/components/FilterableListView/SearchCountBar"
import SearchifyActionButtons from "app/static/frontend/search-new/components/FilterableListView/SearchifyActionButtons"
import SearchifyFooter from "app/static/frontend/search-new/components/FilterableListView/SearchifyFooter"
import TableView from "app/static/frontend/search-new/containers/TableView/index"

import { useResponsiveSize } from "@/hooks"

// constants
import { QuorumTheme } from "app/static/frontend/design-constants"

class FilterableListView extends Component {
    static propTypes = {
        // passed from filterablelistviews.js
        inlineComponent: PropTypes.func.isRequired,
        filterSetComponent: PropTypes.func.isRequired,
        getRefreshablePermanentFilters: PropTypes.func,
        groupedActionButtons: PropTypes.array,
        searchPlaceholderText: PropTypes.string,
        actionButtons: PropTypes.array,
        leftActionButtons: PropTypes.array,
        leftGroupedActionButtons: PropTypes.array,
        rightGroupedActionButtons: PropTypes.array,
        bulkActionsConsole: PropTypes.func,
        visualizationConsole: PropTypes.func,
        filterCurrentData: PropTypes.func,
        // passed from shared/search/filterablelistview/index
        quorumDataType: PropTypes.number,
        resource: PropTypes.object.isRequired,
        isProfile: PropTypes.bool,
        // passed via app/static/frontend/search-new/Search.js or FLV implementation
        uniqueFlvId: PropTypes.string.isRequired,
        labelSingular: PropTypes.string,
        labelPlural: PropTypes.string,
        // passed via app/static/frontend/search-new/containers/filterablelistview/index
        currentFilters: PropTypes.object.isRequired,
        hasNewFiltersOnInitialLoad: PropTypes.bool.isRequired,
        isDisplayOpen: PropTypes.bool.isRequired,
        isTableView: PropTypes.bool.isRequired,
        isOnlyTableView: PropTypes.bool,
        listViewScrollTop: PropTypes.number,
        modifySearchQueryURL: PropTypes.func.isRequired,
        searchTerm: PropTypes.string,
        setNextSearchURL: PropTypes.func.isRequired,
        setSearchTerm: PropTypes.func.isRequired,
        shouldHideFilters: PropTypes.bool,
        storeScrollTopPositions: PropTypes.func.isRequired,
        stanceOptions: PropTypes.arrayOf(PropTypes.object),
        // (actions from mapDispatchToProps)
        postProfileAlignment: PropTypes.func,
        removeResourceTableRow: PropTypes.func,
        segue: PropTypes.func,
        segueToEditBill: PropTypes.func,
        segueToEditComment: PropTypes.func,
        segueToEditContact: PropTypes.func,
        segueToEditDonation: PropTypes.func,
        segueToEditInteraction: PropTypes.func,
        segueToEditOfficial: PropTypes.func,
        segueToEditOrganization: PropTypes.func,
        segueToEditRegulation: PropTypes.func,
        segueToEditRelationship: PropTypes.func,
        segueToModifyOrganization: PropTypes.func,
        segueToOutboxCompose: PropTypes.func,
        segueToOutboxComposeSupporter: PropTypes.func,
        setOrCreatePriority: PropTypes.func,
        setOrCreateStance: PropTypes.func,
        updateResourceTableRow: PropTypes.func,
        setMobileFiltersOpenStatus: PropTypes.func,

        // passed via app/static/frontend/search-new/containers/FilterableListView/index.js
        isLoading: PropTypes.bool.isRequired,
        // passed via shared/search/containers/FilterableListView/index
        isLoadingMore: PropTypes.bool.isRequired,
        isBulkActions: PropTypes.bool,
        isSearchify: PropTypes.bool.isRequired,
        // passed via shared/search/components/FilterableListView/index.js
        reachedEndOfData: PropTypes.bool.isRequired,
        hasNoResults: PropTypes.bool.isRequired,
        // selected from the search => props.uniqueFlvId slice of the store
        searchFilterTerm: PropTypes.string,
        // mobile-specific
        areMobileSearchFiltersOpen: PropTypes.bool,
    }

    componentDidMount() {
        // upon mount, modify the URL to reflect the state of the filters! (if default
        // filters or initial filters are applied)
        this.props.applyFiltersFromURL(this.props.uniqueFlvId)

        if (this.props.searchTerm && this.props.isSearchify) {
            // if we're in Searchify, clear the search bar term on mount and unmount.
            // this is so that Searchify can use the search bar without
            // influencing the search results.
            this.props.setSearchTerm(undefined)
        }

        // scrollTop will only show up if isLoading state is never triggered; otherwise,
        // it'll be quickly overridden by a loading spinner.
        if (this.props.listViewScrollTop && !this.props.hasNewFiltersOnInitialLoad) {
            this.refs.listViewScrollContainer.scrollTop = this.props.listViewScrollTop
        }
    }

    componentDidUpdate(oldProps) {
        if (!this.props.isSearchify && !oldProps.currentFilters.equals(this.props.currentFilters)) {
            this.props.modifySearchQueryURL(this.props.uniqueFlvId)
        }

        if (this.props.isDisplayOpen && !oldProps.isDisplayOpen) {
            this.animateScrollToTop(400)
        }
    }

    componentWillUnmount() {
        if (this.props.searchTerm && this.props.isSearchify) {
            // if we're in Searchify, clear the search bar term on mount and unmount.
            // this is so that Searchify can use the search bar without
            // influencing the search results.
            this.props.setSearchTerm(undefined)
        }

        if (!this.props.isSearchify && !this.props.isProfile) {
            this.props.storeScrollTopPositions(this.props.uniqueFlvId, {
                listView: this.refs.listViewScrollContainer.scrollTop,
            })
        }

        if (this.props.isMobile) {
            this.props.setMobileFiltersOpenStatus(false)
        }
    }

    animateScrollToTop(duration) {
        const element = this.refs.listViewScrollContainer

        if (duration <= 0) {
            return
        }

        const perTick = (element.scrollTop / duration) * 10

        setTimeout(() => {
            element.scrollTop = element.scrollTop - perTick
            return element.scrollTop <= 0 || this.animateScrollToTop(duration - 10)
        }, 10)
    }

    searchCounterHeaderWidth() {
        if (this.props.isSearchify) {
            return 7
        } else {
            return this.props.wide_counter ? 4 : 2
        }
    }

    searchButtonsHeaderWidth() {
        if (this.props.isSearchify) {
            return 5
        } else {
            return this.props.wide_counter ? 8 : 10
        }
    }

    getListView() {
        return (
            <ListView
                filterCurrentData={this.props.filterCurrentData}
                getRefreshablePermanentFilters={this.props.getRefreshablePermanentFilters}
                inlineComponent={this.props.inlineComponent}
                isMobile={this.props.isMobile}
                isProfile={this.props.isProfile}
                isSearchify={this.props.isSearchify}
                noResultDetails={this.props.noResultDetails}
                orientation={this.props.orientation}
                resource={this.props.resource}
                uniqueFlvId={this.props.uniqueFlvId}
            />
        )
    }

    getTableView() {
        return (
            <TableView
                id="Search"
                key="Search"
                isProfile={this.props.isProfile}
                quorumDataType={this.props.quorumDataType}
                resource={this.props.resource}
                stanceOptions={this.props.stanceOptions}
                uniqueFlvId={this.props.uniqueFlvId}
                // actions from mapDispatchToProps
                postProfileAlignment={this.props.postProfileAlignment}
                removeResourceTableRow={this.props.removeResourceTableRow}
                segue={this.props.segue}
                segueToEditBill={this.props.segueToEditBill}
                segueToEditComment={this.props.segueToEditComment}
                segueToEditContact={this.props.segueToEditContact}
                segueToEditDonation={this.props.segueToEditDonation}
                segueToEditInteraction={this.props.segueToEditInteraction}
                segueToEditOfficial={this.props.segueToEditOfficial}
                segueToEditOrganization={this.props.segueToEditOrganization}
                segueToEditPoliticalCommittee={this.props.segueToEditPoliticalCommittee}
                segueToEditRegulation={this.props.segueToEditRegulation}
                segueToEditRelationship={this.props.segueToEditRelationship}
                segueToModifyOrganization={this.props.segueToModifyOrganization}
                segueToOutboxCompose={this.props.segueToOutboxCompose}
                segueToOutboxComposeSupporter={this.props.segueToOutboxComposeSupporter}
                setOrCreatePriority={this.props.setOrCreatePriority}
                setOrCreateStance={this.props.setOrCreateStance}
                updateResourceTableRow={this.props.updateResourceTableRow}
            />
        )
    }

    getFilterSet() {
        const FilterSetComponent = this.props.filterSetComponent

        return (
            <FilterSetComponent
                connectSearchBar={!this.props.isProfile}
                isProfile={this.props.isProfile}
                labelPlural={this.props.labelPlural}
                quorumDataType={this.props.quorumDataType}
                searchFilterTerm={this.props.searchFilterTerm}
                searchPlaceholderText={this.props.searchPlaceholderText}
                uniqueFlvId={this.props.uniqueFlvId}
            />
        )
    }

    getListViewDisplay() {
        return (
            <ListViewDisplayWrapper
                uniqueFlvId={this.props.uniqueFlvId}
                quorumDataType={this.props.quorumDataType}
                resource={this.props.resource}
                bulkActionsConsole={this.props.bulkActionsConsole}
                visualizationConsole={this.props.visualizationConsole}
            />
        )
    }

    getCountBar() {
        return (
            <SearchCountBar
                isBulkActions={this.props.isBulkActions}
                isSearchify={this.props.isSearchify}
                labelPlural={this.props.labelPlural}
                labelSingular={this.props.labelSingular}
                quorumDataType={this.props.quorumDataType}
                uniqueFlvId={this.props.uniqueFlvId}
            />
        )
    }

    getSearchifyFooterBar() {
        return <SearchifyFooter uniqueFlvId={this.props.uniqueFlvId} isMobile={this.props.isMobile} />
    }

    renderTopBar() {
        return (
            <div>
                {this.getCountBar()}
                {this.props.actionButtons && !this.props.isSearchify && (
                    <SearchActionButtons
                        actionButtons={this.props.actionButtons}
                        quorumDataType={this.props.quorumDataType}
                        resource={this.props.resource}
                        uniqueFlvId={this.props.uniqueFlvId}
                    />
                )}
            </div>
        )
    }

    /**
     * Render the search bar in the mobile view.
     *
     * This uses a Mantine component and some quick and dirty sx styling to get it to match the Searchbar on the
     * MobileLandingPage. We should absolutely not be doing this, but we are in crunch time to deliver for Big G.
     */
    renderSearchBar() {
        const dataset = DjangIO.app.models.QuorumDataType.by_value(this.props.quorumDataType).plural
        return (
            <div
                style={{
                    padding: "0 1.5rem 0.75rem",
                    display: !this.props.areMobileSearchFiltersOpen ? null : "none",
                }}
            >
                <TextInput
                    icon={<Icon icon="search" iconFamily="far" color={QuorumTheme.colors.violet[5]} />}
                    size="lg"
                    placeholder={`Search ${dataset.toLowerCase()}...`}
                    sx={{
                        input: {
                            boxShadow: "0px 2px 4px -2px rgba(0, 0, 0, 0.12);",
                            border: `1px solid ${QuorumTheme.colors.gray[2]}`,
                        },
                    }}
                    defaultValue={this.props.searchTerm}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            this.props.setSearchTerm(e.target.value)
                            this.props.updateFilter(
                                "AdvancedSearchFilter",
                                this.props.uniqueFlvId,
                                e.target.value,
                                Boolean(e.target.value),
                            )

                            this.props.createSafedSearchAndUserAction(
                                {
                                    data_type: this.props.quorumDataType,
                                    filters: [
                                        {
                                            ...this.props.currentFilters.toJS(),
                                            advanced_search: e.target.value,
                                        },
                                    ],
                                },
                                { query: e.target.value },
                                "pwa-search-bar",
                            )
                        }
                    }}
                />
            </div>
        )
    }

    renderFooter() {
        let footerText
        if (this.props.isLoadingMore) {
            footerText = "Loading More Results..."
        } else if (this.props.reachedEndOfData) {
            footerText = "No More Results"
        }

        return !this.props.hasNoResults && !this.props.isLoading ? (
            <div className="infinite-scroll-loading-more">{footerText}</div>
        ) : undefined
    }

    renderBody() {
        // we still have to render the filter set because it renders a filter component
        // that interfaces with our search bar. In a better world we'll rewrite the
        // search bar so that it can connect directly with the corresponding filter
        // system.
        const hasFilterGroups = Boolean(this.props.filterSetComponent.defaultProps.filterGroups.length)

        // Don't render Search Actions on Mobile screen sizes
        const isMobile = this.props.isMobile

        return (
            <div
                className={classNames("flv-content", "ff-search-design", {
                    static: this.props.isProfile,
                })}
            >
                {!isMobile && (
                    <div
                        // this class adjusts the side panel width
                        // without affecting the unflagged width
                        className={classNames(
                            "filter-side-panel-design-system",
                            { "hide-filters": this.props.shouldHideFilters },
                            { "no-filters": !hasFilterGroups },
                        )}
                        data-cy="filter-side-panel-design-system"
                    >
                        {this.getFilterSet()}
                    </div>
                )}
                {isMobile && this.props.areMobileSearchFiltersOpen && (
                    <div
                        className={classNames("filter-side-panel-design-system", {
                            "no-filters": !hasFilterGroups,
                        })}
                        data-cy="filter-side-panel-design-system-mobile"
                    >
                        {this.getFilterSet()}
                    </div>
                )}
                <div
                    className="flv-inlines-search-design"
                    ref="listViewScrollContainer"
                    style={isMobile && this.props.areMobileSearchFiltersOpen ? { display: "none" } : null}
                >
                    {this.props.isSearchify && (
                        <SearchifyActionButtons
                            isMobile={isMobile}
                            setMobileFiltersOpenStatus={this.props.setMobileFiltersOpenStatus}
                            uniqueFlvId={this.props.uniqueFlvId}
                            hasFilterGroups={hasFilterGroups}
                        />
                    )}
                    {!isMobile && this.props.actionButtons && !this.props.isSearchify && (
                        <SearchActionButtons
                            actionButtons={this.props.actionButtons}
                            groupedActionButtons={this.props.groupedActionButtons}
                            isProfile={this.props.isProfile}
                            isTableView={this.props.isTableView}
                            isOnlyTableView={this.props.isOnlyTableView}
                            leftActionButtons={this.props.leftActionButtons}
                            leftGroupedActionButtons={this.props.leftGroupedActionButtons}
                            quorumDataType={this.props.quorumDataType}
                            resource={this.props.resource}
                            rightGroupedActionButtons={this.props.rightGroupedActionButtons}
                            shouldHideFilters={this.props.shouldHideFilters}
                            uniqueFlvId={this.props.uniqueFlvId}
                        />
                    )}
                    {this.props.isDisplayOpen && this.getListViewDisplay()}
                    {/* TODO: table view is not currently supported in searchify; we should support it */}
                    {!this.props.isSearchify &&
                    // Also not supported in Mobile, and we _won't_ support it
                    !isMobile &&
                    this.props.isTableView &&
                    // TODO: table view is not currently supported for vote result, we should support it
                    this.props.quorumDataType !== DjangIO.app.models.QuorumDataType.vote_result.value
                        ? this.getTableView()
                        : this.getListView()}
                    {this.renderFooter()}
                </div>
            </div>
        )
    }

    render() {
        return (
            <div
                id={this.props.uniqueFlvId}
                className={classNames({
                    "filterable-list-view": true,
                    [this.props.className]: this.props.className != null,
                    "z-index-1": this.props.isMobile && this.props.areMobileSearchFiltersOpen,
                })}
            >
                {this.props.isSearchify && this.renderTopBar()}
                {this.props.isMobile && !this.props.isSearchify && this.renderSearchBar()}
                {this.renderBody()}
                {this.props.isSearchify &&
                    (!this.props.isMobile || !this.props.areMobileSearchFiltersOpen) &&
                    this.getSearchifyFooterBar()}
            </div>
        )
    }
}

const withResponsiveSize = (FLV) => (props) => {
    const { isMobile } = useResponsiveSize()
    const [orientation, setOrientation] = useState(window.screen.orientation?.type || null)

    useEffect(() => {
        const handleOrientationChange = (event) => setOrientation(event.target.type)

        window.screen.orientation?.addEventListener("change", handleOrientationChange)

        return () => window.screen.orientation?.removeEventListener("change", handleOrientationChange)
    }, [])

    return <FLV {...props} isMobile={isMobile} orientation={orientation} />
}

export default withResponsiveSize(FilterableListView)
